import * as React from 'react'
import {
  $avatar,
  BaseHubAvatar,
  BaseHubAvatarProps,
} from '~/components/basehub-avatar'
import { format } from 'date-fns'
import { clsx } from 'clsx'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { CommitAuthorAvatar } from '../commit-author'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { ReorderIcon } from '~/components/icons/reorder'
import { ContributorName } from '~/components/contributors-tooltip'

export const PrimitiveCommit = ({
  sidePreference = 'left',
  ...props
}: {
  message: React.ReactNode | undefined
  id: string | null
  date: string
  contributors:
    | string[]
    | {
        ids: string[]
        size: BaseHubAvatarProps['size']
      }
  className?: string
  reordered?: boolean
  disableHover?: boolean
  sidePreference?: 'left' | 'right'
}) => {
  const avatarsProps = React.useMemo(() => {
    if ('ids' in props.contributors) {
      return {
        contributors: props.contributors.ids,
        size: props.contributors.size,
      }
    }
    return {
      contributors: props.contributors,
    }
  }, [props.contributors])

  return (
    <div
      className={clsx(
        props.className,
        'grid grid-cols-[1fr,repeat(5,auto)] text-dark-faint text-[0.75rem] gap-y-1.5 items-center w-full'
      )}
    >
      <span
        className={clsx(
          'col-span-full line-clamp-3 text-xs leading-normal transition-colors min-h-5 relative top-px',
          props.id && !props.disableHover
            ? 'text-dark-muted group-hover/wrapper:underline group-focus/wrapper:underline'
            : 'text-dark-bright',
          `group-hover/wrapper:text-dark-bright group-focus/wrapper:text-dark-bright group-data-[active="true"]/wrapper:text-dark-bright`
        )}
      >
        {props.message || 'No commit message.'}
      </span>
      <ContributorsHug {...avatarsProps}  />
      {props.reordered && (
        <>
          <SimpleTooltip content="Reordered" side="left">
            <span className="px-1 -mx-1 transition-colors hover:text-dark-bright">
              <ReorderIcon />
            </span>
          </SimpleTooltip>
          <span className="size-[0.1875rem] bg-dark-faint rounded-full mb-px mx-1.5" />
        </>
      )}
      {props.id && (
        <>
          <CopyTooltip
            tooltipLabel={{
              idle: 'Copy full commit ID',
              active: 'Copied!',
              minWidth: '64px',
            }}
            side={sidePreference}
            sideOffset={8}
            label={`#${props.id.substring(0, 7)}`}
            copyContent={props.id}
            className="select-text !text-[0.75rem] underline"
          />
          <span className="size-[0.1875rem] bg-dark-faint rounded-full mb-px mx-1.5" />
        </>
      )}
      <SimpleTooltip
        content={format(new Date(props.date), 'MMMM d, yyyy, h:mm a z')}
        side={sidePreference}
        delayDuration={0}
      >
        <span className="col-end-[-1] text-right cursor-default">
          <CommitTime date={props.date} />
        </span>
      </SimpleTooltip>
    </div>
  )
}

export const CommitTime = ({ date }: { date: string }) => {
  const timeDiff = useTimeDiff(new Date(date))
  return <>{timeDiff}</>
}

export const ContributorsHug = ({
  contributors,
  disableTooltip,
  max = 4,
  size = 'small',
  ...props
}: {
  contributors: string[]
  disableTooltip?: boolean
  size?: BaseHubAvatarProps['size']
  max?: number
} & Omit<React.HTMLProps<HTMLSpanElement>, 'size' | 'max'>) => {
  const { slice, hasMoreThanMaxContributors, rest } = React.useMemo(() => {
    const hasMoreThanMaxContributors = contributors.length > max
    const endIndex = hasMoreThanMaxContributors ? max - 1 : max
    return {
      slice: contributors.slice(0, endIndex),
      hasMoreThanMaxContributors,
      rest: contributors.slice(endIndex),
    }
  }, [contributors, max])

  const avatars = React.useMemo(
    () =>
      slice.map((c) => (
        <Avatar key={c} id={c} disableTooltip={disableTooltip} size={size} />
      )),
    [slice, disableTooltip, size]
  )

  const tooltipContent = React.useMemo(
    () =>
      rest.map((id) => (
        <React.Fragment key={id}>
          <ContributorName key={id} id={id} />
          <br />
        </React.Fragment>
      )),
    [rest]
  )

  return (
    <span className="group flex -space-x-1.5" {...props}>
      {avatars}
      {hasMoreThanMaxContributors && (
        <SimpleTooltip
          disabled={disableTooltip}
          delayDuration={100}
          content={tooltipContent}
        >
          <span
            className={$avatar({
              size,
              className: clsx(
                'text-dark-muted !bg-dark-bg border-dark-border-solid -ml-2 p-0.5 !w-auto text-[11px] flex items-center justify-center relative transition-colors',
                disableTooltip || 'hover:!bg-dark-shade-hover'
              ),
              withBorder: true,
            })}
          >
            +{rest.length}
          </span>
        </SimpleTooltip>
      )}
    </span>
  )
}

const Avatar = React.memo(
  ({
    id,
    disableTooltip,
    size,
  }: {
    id: string
    disableTooltip?: boolean
    size: BaseHubAvatarProps['size']
  }) =>
    disableTooltip ? (
      <span>
        <BaseHubAvatar
          name=""
          id={id}
          size={size}
          withBorder
          className="select-none shrink-0"
        />
      </span>
    ) : (
      <CommitAuthorAvatar authorId={id} size={size} />
    )
)
